import { useFeatureFlagConfig } from '@/ui-hooks/useFeatureFlagConfig';
import { FeatureFlagName } from '@diritto/common/system/constants';
import { LocalStorageKey } from '@diritto/common/utils/localStorage';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useModeGuideFABVisibility } from 'ui-hooks/useModeGuideFABVisibility';

interface ModeGuideFABProps {
  children: ReactNode;
}

const BOTTOM_THRESHOLD = 200;

export const ModeGuideFAB = ({ children }: ModeGuideFABProps) => {
  const { showModeGuideFAB } = useModeGuideFABVisibility();
  const [opacity, setOpacity] = useState(1);
  const [isDismissedByClick, setIsDismissedByClick] = useState(false);
  const { isActiveFlag } = useFeatureFlagConfig();
  const isHomeTextModeActive = isActiveFlag(FeatureFlagName.HomeTextMode);

  useEffect(() => {
    if (!showModeGuideFAB) {
      return () => {};
    }

    const checkScrollPosition = () => {
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const distanceFromBottom = documentHeight - (scrollTop + windowHeight);

      if (distanceFromBottom <= BOTTOM_THRESHOLD) {
        setOpacity(0);
      } else if (!isDismissedByClick) {
        setOpacity(1);
      }
    };

    checkScrollPosition();

    window.addEventListener('scroll', checkScrollPosition, { passive: true });

    return () => {
      window.removeEventListener('scroll', checkScrollPosition);
    };
  }, [showModeGuideFAB, isDismissedByClick]);

  const handleClick = useCallback(() => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: 'smooth',
    });

    localStorage.setItem(LocalStorageKey.ModeGuideFABDismissed, 'true');
    setIsDismissedByClick(true);
  }, []);

  if (!isHomeTextModeActive) {
    return null;
  }

  if (!showModeGuideFAB) {
    return null;
  }

  return (
    <button
      onClick={handleClick}
      className={`bg-primary-500 fixed bottom-[72px] pc:bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1 pl-4 pr-3 py-3 rounded-3xl cursor-pointer z-[10] shadow-[0_4px_10px_rgba(69,44,123,0.25)] border border-solid border-primary-400 transition-opacity duration-300 ${opacity === 0 ? 'pointer-events-none' : ''}`}
      style={{ opacity }}>
      {children}
    </button>
  );
};
